/**
 * Check if an action requires reseeding events as evidence.
 * Driven by the `required_evidence` field in the action_evidence table.
 */
export function isReseedingAction(requiredEvidence: string): boolean {
  const types = requiredEvidence.split(",").map((s) => s.trim().toUpperCase());
  return types.includes("RESEED_EVENTS");
}

/**
 * Check if an action requires fertilizer events as evidence.
 */
export function isFertilizerAction(requiredEvidence: string): boolean {
  const types = requiredEvidence.split(",").map((s) => s.trim().toUpperCase());
  return types.includes("FERTILIZER_EVENTS");
}

/**
 * Check if an action requires field preparation events as evidence.
 */
export function isFieldPreparationAction(requiredEvidence: string): boolean {
  const types = requiredEvidence.split(",").map((s) => s.trim().toUpperCase());
  return types.includes("FIELD_PREPARATION_EVENTS");
}

export type EvidenceType = "reseeding" | "fertilizer" | "fieldPreparation";

export function getEvidenceType(requiredEvidence: string): EvidenceType | null {
  if (isReseedingAction(requiredEvidence)) return "reseeding";
  if (isFertilizerAction(requiredEvidence)) return "fertilizer";
  if (isFieldPreparationAction(requiredEvidence)) return "fieldPreparation";
  return null;
}

export type ReseedingAutoEvidenceItem = {
  guid: string;
  farmName: string;
  date: string;
  reseedType: string;
  newGrassType?: string;
  seedingMethod?: string;
  productName?: string;
  fieldGuids: string[];
  fieldNames: string[];
  seedingRate?: number;
  totalAmount?: number;
  totalAmountUnit?: string;
  areaHa?: number;
};

export type FertilizerAutoEvidenceItem = {
  guid: string;
  farmName: string;
  date: string;
  fieldGuids: string[];
  fieldNames: string[];
  totalAmount: number;
  totalAmountUnit: string;
  note?: string;
  fertilizerProperties: {
    fertilizerType: string;
    productName?: string;
    organicFertilizerType?: string;
    applicationMethod?: string;
    [key: string]: unknown;
  };
  areaHa?: number;
};

export type FieldPreparationAutoEvidenceItem = {
  guid: string;
  farmName: string;
  date: string;
  fieldGuids: string[];
  fieldNames: string[];
  machinery?: string[];
  fuel?: number;
  note?: string;
  areaHa?: number;
};

export type AutoEvidenceItem =
  | ReseedingAutoEvidenceItem
  | FertilizerAutoEvidenceItem
  | FieldPreparationAutoEvidenceItem;
